import { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";

import { useAuth } from "../hooks/useAuth";
import {
  fetchInstitutionDashboard,
  resubmitInstitutionDocument,
} from "../api/institutions";
import {
  fetchOrganisationDashboard,
  resubmitOrganisationDocument,
} from "../api/organisations";
import { BrandLogo } from "../components/BrandLogo";
import { FileUpload } from "../components/FileUpload";
import { useIsMobile } from "../hooks/useIsMobile";
import { C, font, radii, space, type } from "../styles/tokens";
import { BrandButton, BrandCard, StepIntro } from "../components/brand";

/**
 * AwaitingApprovalPage
 *
 * Shown to institution / organisation officers whose org is still in
 * PENDING_REVIEW. Polls the dashboard every 30 seconds to check if approval
 * has come through; if yes, redirects to the real dashboard.
 *
 * If the org is REJECTED, shows the rejection reason and a quiet path to
 * contact support.
 */
export function AwaitingApprovalPage() {
  const { localUser, signOut } = useAuth();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const isInstitution = localUser?.type === "INSTITUTION_OFFICER";
  const isOrganisation = localUser?.type === "ORGANISATION_OFFICER";

  const [orgStatus, setOrgStatus] = useState(null);
  const [orgName, setOrgName] = useState(null);
  const [rejectionReason, setRejectionReason] = useState(null);
  const [resubmitting, setResubmitting] = useState(false);
  const [resubmitError, setResubmitError] = useState(null);

  // Initial org info comes from the local user record
  useEffect(() => {
    if (isInstitution) {
      const inst = localUser?.institutionOfficer?.institution;
      setOrgStatus(inst?.status);
      setOrgName(inst?.name);
      setRejectionReason(inst?.rejectionReason);
    } else if (isOrganisation) {
      const org = localUser?.organisationOfficer?.organisation;
      setOrgStatus(org?.status);
      setOrgName(org?.name);
      setRejectionReason(org?.rejectionReason);
    }
  }, [localUser, isInstitution, isOrganisation]);

  // Poll for status updates every 30 seconds (only when still pending)
  useEffect(() => {
    if (orgStatus !== "PENDING_REVIEW") return;
    const fetcher = isInstitution
      ? fetchInstitutionDashboard
      : fetchOrganisationDashboard;
    if (!fetcher) return;

    const interval = setInterval(async () => {
      try {
        const data = await fetcher();
        const org = data.institution ?? data.organisation;
        if (org?.status === "APPROVED") {
          navigate("/dashboard", { replace: true });
        } else {
          setOrgStatus(org?.status);
          setRejectionReason(org?.rejectionReason);
        }
      } catch {
        // Ignore polling errors silently - we'll retry on the next tick
      }
    }, 30_000);

    return () => clearInterval(interval);
  }, [orgStatus, isInstitution, navigate]);

  if (!isInstitution && !isOrganisation) {
    // Not an officer - this page doesn't apply
    return <Navigate to="/profile" replace />;
  }
  // Officer signed up but hasn't completed the institution/organisation details
  // form yet -- send them back to finish.
  if (isInstitution && !localUser?.institutionOfficer) {
    return <Navigate to="/register/institution/details" replace />;
  }
  if (isOrganisation && !localUser?.organisationOfficer) {
    return <Navigate to="/register/organisation/details" replace />;
  }

  return (
    <div
      style={{
        minHeight: "100vh",
        backgroundColor: C.background,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: "24px 16px",
        fontFamily: font,
        color: C.textStrong,
        boxSizing: "border-box",
      }}
    >
      <div style={{ width: "100%", maxWidth: 540 }}>
        {/* Centered Brand Mark per Section 7 */}
        <div style={{ textAlign: "center", marginBottom: space.xl }}>
          <img
            src="/brand-mark.png"
            alt="TickAndPass"
            style={{ height: 32, width: "auto", objectFit: "contain" }}
          />
        </div>

        <BrandCard style={{ padding: isMobile ? space.lg : space.xxl, textAlign: "center" }}>
          {orgStatus === "PENDING_REVIEW" && (
            <PendingContent
              orgName={orgName}
              email={localUser?.email}
              isMobile={isMobile}
              requiresManualApproval={Boolean(
                localUser?.requiresManualApproval,
              )}
            />
          )}
          {orgStatus === "REJECTED" && (
            <RejectedContent
              orgName={orgName}
              reason={rejectionReason}
              email={localUser?.email}
              isInstitution={isInstitution}
              resubmitting={resubmitting}
              resubmitError={resubmitError}
              isMobile={isMobile}
              onResubmit={async (uploaded) => {
                if (!uploaded?.s3Key) return;
                setResubmitting(true);
                setResubmitError(null);
                try {
                  const fn = isInstitution
                    ? resubmitInstitutionDocument
                    : resubmitOrganisationDocument;
                  await fn(uploaded.s3Key);
                  // Status flipped server-side; reflect locally and stop showing
                  // the rejection UI until the next admin decision.
                  setOrgStatus("PENDING_REVIEW");
                  setRejectionReason(null);
                } catch (err) {
                  setResubmitError(err?.message ?? "Resubmit failed.");
                } finally {
                  setResubmitting(false);
                }
              }}
            />
          )}
          {orgStatus === "APPROVED" && (
            <ApprovedContent
              isMobile={isMobile}
              onContinue={() => navigate("/dashboard", { replace: true })}
            />
          )}
          {orgStatus === "SUSPENDED" && (
            <SuspendedContent orgName={orgName} isMobile={isMobile} />
          )}

          <div
            style={{
              marginTop: space.xl,
              paddingTop: space.md,
              borderTop: `1px solid ${C.divider}`,
              display: "flex",
              justifyContent: "center",
            }}
          >
            <BrandButton
              variant="text"
              size="small"
              onClick={signOut}
            >
              Sign out
            </BrandButton>
          </div>
        </BrandCard>
      </div>
    </div>
  );
}

function PendingContent({ orgName, email, requiresManualApproval, isMobile }) {
  return (
    <>
      <StepIntro
        Icon={ClockIcon}
        title="Application under review"
        body={`Thanks for registering ${orgName ?? "your organisation"}. Our team is reviewing your documents (typically 1-3 business days).`}
      />
      {requiresManualApproval && (
        <div
          style={{
            padding: space.md,
            backgroundColor: C.warningTint,
            color: C.warning,
            borderRadius: radii.control,
            margin: `${space.md}px 0`,
            fontSize: type.caption.fontSize,
            lineHeight: type.caption.lineHeight,
            textAlign: "left",
          }}
        >
          <div style={{ fontWeight: 600, marginBottom: 2 }}>
            Review Reason: Individual PAN
          </div>
          <div>
            This account was registered using an Individual-category PAN. A
            platform administrator must manually review and approve the
            organisation profile before full platform features are unlocked.
          </div>
        </div>
      )}
      <p style={{ fontSize: type.caption.fontSize, color: C.textSubtle, marginTop: space.sm }}>
        We'll email <strong>{email}</strong> as soon as we have an update. You
        can close this tab and come back later &mdash; the page refreshes
        automatically.
      </p>
    </>
  );
}

function RejectedContent({
  orgName,
  reason,
  email,
  isInstitution,
  resubmitting,
  resubmitError,
  onResubmit,
  isMobile,
}) {
  return (
    <>
      <StepIntro
        Icon={XIcon}
        title="Application not approved"
        body={`We weren't able to approve ${orgName ?? "your organisation"} at this time.`}
      />
      {reason && (
        <div
          style={{
            padding: space.md,
            backgroundColor: C.dangerTint,
            borderRadius: radii.control,
            margin: `${space.md}px 0`,
            fontSize: type.caption.fontSize,
            color: C.danger,
            lineHeight: type.caption.lineHeight,
            textAlign: "left",
            overflowWrap: "anywhere",
            wordBreak: "break-word",
          }}
        >
          <div style={{ fontWeight: 600, marginBottom: 4 }}>Reason:</div>
          {reason}
        </div>
      )}

      {/* Re-upload zone -- replaces the registration document and flips the
          status back to PENDING_REVIEW for another admin review. */}
      <div style={{ textAlign: "left", margin: `${space.md}px 0 ${space.sm}px` }}>
        <div
          style={{
            fontSize: type.label.fontSize,
            fontWeight: 600,
            color: C.textStrong,
            marginBottom: 6,
          }}
        >
          {isInstitution
            ? "Upload an updated institution registration document"
            : "Upload an updated organisation registration document"}
        </div>
        <FileUpload
          category="registration-document"
          onUpload={onResubmit}
          disabled={resubmitting}
        />
        {resubmitting && (
          <div style={{ fontSize: type.caption.fontSize, color: C.textMuted, marginTop: 6 }}>
            Submitting for re-review...
          </div>
        )}
        {resubmitError && (
          <div style={{ fontSize: type.caption.fontSize, color: C.danger, marginTop: 6 }}>
            {resubmitError}
          </div>
        )}
      </div>

      <p
        style={{
          fontSize: type.caption.fontSize,
          color: C.textSubtle,
          marginTop: space.md,
        }}
      >
        Or contact us at{" "}
        <a href="mailto:support@tickandpass.com" style={{ color: C.primary, textDecoration: "underline" }}>
          support@tickandpass.com
        </a>{" "}
        from <strong>{email}</strong> if you believe this was a mistake.
      </p>
    </>
  );
}

function ApprovedContent({ onContinue, isMobile }) {
  return (
    <>
      <StepIntro
        Icon={CheckIcon}
        title="You're approved!"
        body="Your organisation is approved and ready to start issuing and verifying credentials."
      />
      <div style={{ marginTop: space.lg }}>
        <BrandButton variant="primary" fullWidth onClick={onContinue}>
          Go to dashboard &rarr;
        </BrandButton>
      </div>
    </>
  );
}

function SuspendedContent({ orgName, isMobile }) {
  return (
    <>
      <div style={iconCircle(C.redLight, C.red)}>
        <XIcon />
      </div>
      <h1
        style={{
          ...titleStyle,
          fontSize: isMobile ? 18 : 20,
          overflowWrap: "anywhere",
          wordBreak: "break-word",
        }}
      >
        Account suspended
      </h1>
      <p
        style={{
          ...bodyStyle,
          overflowWrap: "anywhere",
          wordBreak: "break-word",
        }}
      >
        <strong>{orgName ?? "Your organisation"}</strong> has been suspended.
        Contact{" "}
        <a href="mailto:support@tickandpass.com" style={{ color: C.blue }}>
          support@tickandpass.com
        </a>{" "}
        for assistance.
      </p>
    </>
  );
}

// Icons
function ClockIcon() {
  return (
    <svg
      width="28"
      height="28"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  );
}
function CheckIcon() {
  return (
    <svg
      width="28"
      height="28"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.4"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}
function XIcon() {
  return (
    <svg
      width="28"
      height="28"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.4"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

const pageStyle = {
  minHeight: "100vh",
  background: C.bgMuted,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 16,
  fontFamily: font,
  color: C.textDark,
};

const cardStyle = {
  background: C.bg,
  border: `1px solid ${C.divider}`,
  borderRadius: 8,
  padding: 32,
  textAlign: "center",
};

const titleStyle = { fontSize: 20, fontWeight: 700, margin: "0 0 8px" };
const bodyStyle = {
  fontSize: 14,
  color: C.textMed,
  lineHeight: 1.5,
  margin: "8px 0",
};

const iconCircle = (bg, color) => ({
  width: 56,
  height: 56,
  borderRadius: "50%",
  background: bg,
  color,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  margin: "0 auto 16px",
});
